import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Music, Plus, Edit3, Trash2, LogOut, Loader2, ListMusic, FileText, Settings, LayoutDashboard, GripVertical, Pin, PinOff, ShieldAlert, Shield, Trash, Power, Radio, Search, Tag, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { releaseLabel } from '@/constants/releaseTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminReleasesPage = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);
  const [busySweep, setBusySweep] = useState(false);
  const [busyCheckout, setBusyCheckout] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        setCheckoutEnabled(data?.checkout_enabled !== false);
      } catch {}
    })();
  }, []);

  const toggleCheckout = async () => {
    if (!window.confirm(checkoutEnabled ? 'Disable checkout? Buyers will see a 503 message. Site stays browseable.' : 'Re-enable checkout?')) return;
    setBusyCheckout(true);
    try {
      await axios.put(`${API}/settings`, { checkout_enabled: !checkoutEnabled });
      setCheckoutEnabled(!checkoutEnabled);
      toast.success(!checkoutEnabled ? 'Checkout ENABLED' : 'Checkout DISABLED');
    } catch {
      toast.error('Failed to update setting');
    } finally {
      setBusyCheckout(false);
    }
  };

  const runStorageSweep = async () => {
    if (!window.confirm('Purge every storage object not referenced by any album or setting? Active files stay locked. This cannot be undone.')) return;
    setBusySweep(true);
    try {
      const { data } = await axios.post(`${API}/admin/storage/sweep`, {});
      const mb = (data.freed_bytes / (1024 * 1024)).toFixed(1);
      toast.success(`Freed ${mb} MB · deleted ${data.deleted} of ${data.attempted || data.deleted} unused files · kept ${data.kept_referenced} referenced`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Storage sweep failed');
    } finally {
      setBusySweep(false);
    }
  };
  const [albums, setAlbums] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [albumsRes, genresRes] = await Promise.all([
        axios.get(`${API}/albums`),
        axios.get(`${API}/genres`),
      ]);
      // Server already sorts pinned-first then newest — respect that order.
      // Mark _pinned client-side so we can render the pin/unpin state and drag hint
      const withPinned = (albumsRes.data || []).map((a) => ({
        ...a,
        _pinned: a.sort_order !== null && a.sort_order !== undefined,
      }));
      setAlbums(withPinned);
      setGenres(genresRes.data);
    } catch (e) {
      toast.error('Failed to load releases');
    } finally {
      setLoading(false);
    }
  };

  const persistOrder = async (nextAlbums) => {
    setSavingOrder(true);
    // Only include manually-pinned items in the payload — new/date-sorted items keep null sort_order
    const pinned = nextAlbums.filter((a) => a._pinned).map((a) => a.id);
    try {
      await axios.put(`${API}/admin/albums/reorder`, { ids: pinned });
      toast.success('Order saved');
    } catch {
      toast.error('Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (targetIdx) => {
    if (dragIndex === null || dragIndex === targetIdx) return;
    const next = [...albums];
    const [moved] = next.splice(dragIndex, 1);
    moved._pinned = true; // dragging pins it
    next.splice(targetIdx, 0, moved);
    setAlbums(next);
    setDragIndex(null);
    persistOrder(next);
  };
  const togglePin = (album) => {
    const next = albums.map((a) =>
      a.id === album.id ? { ...a, _pinned: !a._pinned } : a
    );
    setAlbums(next);
    persistOrder(next);
  };

  const genreName = (id) => genres.find((g) => g.id === id)?.name || '—';
  const genreNamesFor = (album) => {
    const ids = album.genre_ids || (album.genre_id ? [album.genre_id] : []);
    return ids.map(genreName).filter(Boolean).join(', ') || '—';
  };

  const resolveCover = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

  const handleDelete = async (album) => {
    if (!window.confirm(`Delete "${album.title}" by ${album.artist}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/albums/${album.id}`);
      setAlbums(albums.filter((a) => a.id !== album.id));
      toast.success('Release deleted');
    } catch (e) {
      toast.error('Failed to delete release');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Music className="text-[#00F0FF]" size={32} />
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="releases-title">My Releases</h1>
              <p className="text-[#A1A1AA]">Manage every album and single in your store</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end max-w-[70%]">
            {admin?.email && <span className="hidden xl:inline text-xs text-[#71717A] mr-2">{admin.email}</span>}
            <Button
              onClick={() => navigate('/admin/upload')}
              className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
              data-testid="new-release-button"
            >
              <Plus size={16} className="mr-2" /> New Release
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/orders/review')}
              className="border-[#FFB020]/40 text-[#FFB020] hover:bg-[#FFB020]/10 rounded-full"
              data-testid="nav-review-orders-button"
            >
              <ShieldAlert size={16} className="mr-2" /> Review Orders
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/blocklist')}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full"
              data-testid="nav-blocklist-button"
            >
              <Shield size={16} className="mr-2" /> Blocklist
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/discount-urls')}
              className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-full"
              data-testid="nav-discount-urls-button"
            >
              <Tag size={16} className="mr-2" /> Discount URLs
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/live-visitors')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-live-visitors-button"
            >
              <Radio size={16} className="mr-2" /> Live Visitors
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/visitor-history')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-visitor-history-button"
            >
              <History size={16} className="mr-2" /> Visitor History
            </Button>
            <Button
              variant="outline"
              onClick={toggleCheckout}
              disabled={busyCheckout}
              className={`rounded-full ${checkoutEnabled ? 'border-[#27272A] hover:bg-[#18181B]' : 'border-red-500 bg-red-500/10 text-red-400 hover:bg-red-500/20'}`}
              data-testid="nav-checkout-toggle-button"
            >
              {busyCheckout ? <Loader2 className="animate-spin mr-2" size={16} /> : <Power size={16} className="mr-2" />}
              {checkoutEnabled ? 'Checkout ON' : 'Checkout OFF'}
            </Button>
            <Button
              variant="outline"
              onClick={runStorageSweep}
              disabled={busySweep}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-storage-sweep-button"
            >
              {busySweep ? <Loader2 className="animate-spin mr-2" size={16} /> : <Trash size={16} className="mr-2" />}
              Storage Sweep
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-dashboard-button"
            >
              <LayoutDashboard size={16} className="mr-2" /> Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/pages')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-pages-button"
            >
              <FileText size={16} className="mr-2" /> Site Pages
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/settings')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="nav-settings-button"
            >
              <Settings size={16} className="mr-2" /> Settings
            </Button>
            <Button
              variant="outline"
              onClick={async () => { await logout(); navigate('/'); }}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="admin-logout-button"
            >
              <LogOut size={16} className="mr-2" /> Log out
            </Button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="text-[#00F0FF] animate-spin" size={40} />
          </div>
        ) : albums.length === 0 ? (
          <div className="text-center py-24">
            <ListMusic className="mx-auto mb-4 text-[#27272A]" size={64} />
            <p className="text-[#A1A1AA] mb-4">No releases yet</p>
            <Button
              onClick={() => navigate('/admin/upload')}
              className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
            >
              <Plus size={16} className="mr-2" /> Upload your first release
            </Button>
          </div>
        ) : (
          <div className="bg-[#121214] border border-[#27272A] rounded-lg overflow-hidden">
            <div className="px-4 md:px-6 py-3 border-b border-[#27272A] flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search releases by title, artist, or genre…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-[#0F0F11] border border-[#27272A] rounded-md focus:border-[#00F0FF] focus:outline-none"
                  data-testid="release-search"
                />
              </div>
              <p className="text-xs text-[#71717A] hidden md:block">
                Drag rows to pin an old release at the top.
              </p>
              {savingOrder && (
                <span className="text-xs text-[#00F0FF] inline-flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> Saving order…
                </span>
              )}
            </div>
            {(() => {
              const rx = query.trim().toLowerCase();
              const filteredAlbums = !rx ? albums : albums.filter((a) =>
                (a.title || '').toLowerCase().includes(rx) ||
                (a.artist || '').toLowerCase().includes(rx) ||
                genreNamesFor(a).toLowerCase().includes(rx)
              );
              if (filteredAlbums.length === 0) {
                return (
                  <div className="px-6 py-16 text-center text-sm text-[#71717A]" data-testid="release-search-empty">
                    No releases match "{query}".
                  </div>
                );
              }
              return (
                <>
                  <div className="hidden md:grid grid-cols-[24px_80px_1fr_auto_auto_auto_auto_36px_auto] gap-4 items-center px-6 py-3 border-b border-[#27272A] text-xs font-medium text-[#A1A1AA] uppercase tracking-wider">
              <span></span>
              <span>Cover</span>
              <span>Title / Artist</span>
              <span>Type</span>
              <span>Genre</span>
              <span>Tracks</span>
              <span>Price</span>
              <span>Pin</span>
              <span className="text-right">Actions</span>
            </div>

            {filteredAlbums.map((album, i) => (
              <div
                key={album.id}
                draggable={!rx}
                onDragStart={() => !rx && handleDragStart(i)}
                onDragOver={handleDragOver}
                onDrop={() => !rx && handleDrop(i)}
                className={`grid grid-cols-[24px_80px_1fr_auto_auto_auto_auto_36px_auto] gap-4 items-center px-4 md:px-6 py-4 border-b border-[#27272A] last:border-b-0 transition-colors hidden md:grid ${
                  dragIndex === i ? 'bg-[#00F0FF]/5' : 'hover:bg-[#18181B]'
                } ${album._pinned ? 'border-l-2 border-l-[#00F0FF]' : ''}`}
                data-testid={`release-row-${album.id}`}
              >
                <button
                  className="text-[#52525B] hover:text-white cursor-grab active:cursor-grabbing"
                  title="Drag to reorder"
                  data-testid={`drag-handle-${album.id}`}
                  aria-label={`Reorder ${album.title}`}
                >
                  <GripVertical size={16} />
                </button>
                <img
                  src={resolveCover(album.cover_url)}
                  alt={album.title}
                  className="w-14 h-14 rounded object-cover bg-[#18181B]"
                />
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {album.title}
                    {album._pinned && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">
                        Pinned
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[#A1A1AA] truncate">{album.artist}</div>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-[#18181B] border border-[#27272A]">
                  {releaseLabel(album.release_type)}
                </span>
                <span className="text-sm text-[#A1A1AA] truncate max-w-[180px]" title={genreNamesFor(album)}>{genreNamesFor(album)}</span>
                <span className="text-sm text-[#A1A1AA]">{album.tracks?.length || 0}</span>
                <span className="text-[#00F0FF] font-medium">₱{album.price.toFixed(2)}</span>
                <button
                  onClick={() => togglePin(album)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                    album._pinned
                      ? 'text-[#00F0FF] bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20'
                      : 'text-[#52525B] hover:text-white hover:bg-[#27272A]'
                  }`}
                  title={album._pinned ? 'Unpin (return to newest-first)' : 'Pin to top'}
                  data-testid={`pin-toggle-${album.id}`}
                >
                  {album._pinned ? <Pin size={14} /> : <PinOff size={14} />}
                </button>
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin/releases/${album.id}/edit`)}
                    className="border-[#27272A] hover:bg-[#27272A] rounded-full"
                    data-testid={`edit-release-${album.id}`}
                  >
                    <Edit3 size={14} className="mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(album)}
                    className="border-[#27272A] hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444] rounded-full"
                    data-testid={`delete-release-${album.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}

            {/* Mobile card list */}
            <div className="md:hidden">
              {filteredAlbums.map((album) => (
                <div key={album.id} className={`flex gap-3 p-4 border-b border-[#27272A] last:border-b-0 ${album._pinned ? 'border-l-2 border-l-[#00F0FF]' : ''}`} data-testid={`release-card-${album.id}`}>
                  <img
                    src={resolveCover(album.cover_url)}
                    alt={album.title}
                    className="w-16 h-16 rounded object-cover bg-[#18181B] shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">{album.title}</span>
                      {album._pinned && (
                        <Pin size={10} className="text-[#00F0FF]" />
                      )}
                    </div>
                    <div className="text-xs text-[#A1A1AA] truncate">{album.artist}</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px] text-[#71717A]">
                      <span className="text-[#00F0FF] font-medium">₱{album.price.toFixed(2)}</span>
                      <span>·</span>
                      <span>{album.tracks?.length || 0} tracks</span>
                      <span>·</span>
                      <span>{genreNamesFor(album)}</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => navigate(`/admin/releases/${album.id}/edit`)}
                        className="text-[10px] px-3 py-1 rounded-full border border-[#27272A] hover:border-[#00F0FF]"
                      >
                        <Edit3 size={10} className="inline mr-1" /> Edit
                      </button>
                      <button
                        onClick={() => togglePin(album)}
                        className={`text-[10px] px-3 py-1 rounded-full border ${album._pinned ? 'border-[#00F0FF] text-[#00F0FF]' : 'border-[#27272A]'}`}
                      >
                        {album._pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button
                        onClick={() => handleDelete(album)}
                        className="text-[10px] px-3 py-1 rounded-full border border-[#27272A] text-red-400 hover:border-red-500"
                      >
                        <Trash2 size={10} className="inline mr-1" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReleasesPage;
